import React, { useState, useEffect, useRef } from 'react'

import Processing     from '../Processing'
import Tribute        from '../Tribute'
import DataSharing    from '../DataSharing'
import EmployerMatching from '../EmployerMatching'
import EmployerMatchingStatus from '../EmployerMatchingStatus'
import Intro          from '../Intro'
import Amount         from './Amount'
import Subscription   from './Subscription'
import CoverCosts     from './CoverCosts'
import Tipping        from './Tipping'
import PaymentOptions from './PaymentOptions'
import Contact        from './Contact'
import CreditCard     from './CreditCard'
import BlackbaudCheckout from './BlackbaudCheckout'
import SubmitButton   from './SubmitButton'
import InvalidForm    from 'src/components/InvalidForm'
import Backdrop       from './Backdrop'
import Summary        from './Summary'

import useModalLock         from 'src/widgets/useModalLock'
import { paypalCurrencySupported } from 'src/payments/paypalCurrency'
import paymentMethodLabel   from '../paymentMethodLabel'
import useBeneficiarySelect from '../useBeneficiarySelect'
import useDonation          from './useDonation'
import useRecord            from './useRecord'
import useValidations       from './useValidations'
import useCreditCard        from './useCreditCard'
import useBlackbaudCheckout from './useBlackbaudCheckout'
import useBank              from './useBank'
import usePaypal            from './usePaypal'
import useVenmo             from './useVenmo'
import useCanadianReceipt   from '../useCanadianReceipt'
import useGiftAid           from '../useGiftAid'

const fiatOrder = [ 'paymentRequest', 'credit_card', 'paypal', 'venmo', 'bank' ]

const App = ({
  creditCardProps, blackbaudCheckoutProps, bankProps, paypalProps, googleApiKey,
  partner_item, partner_items, goal_partner_item, partner, partner_is_pledge,
  token, template, pledge_id, pdt, donationTags, embeddedOn, donationsPath,
  i18n, tribute, fiatPaymentMethods, fiatPaymentMethod,
  amount, order, subscription, frequencies, data_sharing, defaultShares,
  session, foundation, collect_address, collect_phone,
  currencies, presets, min_amount, blank_amount,
  tipping, show_input_labels,
  commonInputProps, setDonation, disable_donation_split,
  stripe, elements, stripeElements, panel,
  traditional_flat_style, plugins, employerMatching,
  sympathy_card_enabled, stripeKey,
  gift_aid_modal_src, widget_instance_token,
  disable_express_checkout,
}) => {
  const [ paymentOptions, setPaymentOptions ] =
    useState(fiatPaymentMethods.credit_card ? null : fiatPaymentMethods)

  const [ paymentOption, setPaymentOption ] =
    useState(paymentOptions && fiatPaymentMethod)

  // Set when a currency change forces us off the donor's chosen method.
  const [ switchedFrom, setSwitchedFrom ] = useState(null)
  const optionsRef = useRef(null)

  const resolveFeeConfig = () => {
    switch (paymentOption) {
    case 'paymentRequest':
    case 'credit_card':
      return (blackbaudCheckoutProps || creditCardProps).feeConfig
    case 'bank':
      return bankProps.feeConfig
    case 'paypal':
    case 'venmo':
      return paypalProps.feeConfig
    }
  }

  const { partnerItem, projectId, ...beneficiarySelect } = useBeneficiarySelect({
    i18n, commonInputProps, partner_item, partner_items, goal_partner_item,
    disable_donation_split,
  })

  const canadianReceipt = useCanadianReceipt({ partnerItem, partner_items })
  const giftAid = useGiftAid({
    partnerItem, partner_items,
    src: gift_aid_modal_src,
    id: `gift-aid:${widget_instance_token}`,
  })

  const blackbaudEnabled = !!blackbaudCheckoutProps

  const donation = useDonation({
    amount, currencies, presets, min_amount, blank_amount,
    order, tipping, collect_address, canadianReceipt, giftAid, subscription,
    feeConfig: resolveFeeConfig(),
  })

  // requireBillingAddress makes Blackbaud's iframe collect name + email + the
  // billing address and ignore billingDetails, so ours come off the transaction
  // at finalize instead (see Widgets::BlackbaudFinalization).
  const hideDonorFields = blackbaudEnabled && donation.collectAddress
  // A logged-in donor's identity stays ours — the server keeps their account and
  // supplies the name — so the draft must not be handed to Blackbaud's donor.
  // collect_email is false only for them.
  const blackbaudCollectsDonor = hideDonorFields && session.collect_email

  const validations = useValidations({
    i18n, session, collect_phone, hideDonorFields,
    currency: donation.currency,
    minAmount: donation.minAmount,
  })

  useEffect(() => {
    validations.donation.resetErrorsFor('amount')
  }, [ donation.state.amount ])

  useEffect(() => {
    validations.tip.resetErrorsFor('tip')
  }, [ donation.tip ])

  const validateAll = ({ contact=true }={}) => {
    // Make sure all validations run
    const results = [
      beneficiarySelect.validate(),
      validations.donation.validate(donation.state),
      tribute.validate(),
      plugins.validate({ donation }),
    ]

    if (tipping.type == 'tipping')
      results.push(validations.tip.validate(donation))

    if (contact)
      results.push(validations.contact.validate(donation.state))

    return results.length == results.filter(Boolean).length
  }

  const record = useRecord({
    i18n, order, partner, partnerItem, projectId, pledge_id, pdt, session, tribute,
    donationTags, embeddedOn, donation, plugins, canadianReceipt, giftAid,
    donationsPath, employerMatching,
    widget: { token, template },
  })

  // Lock the host modal against closing while a payment is processing.
  // record.processing is this panel's in-flight flag (card / wallet-
  // express / ACH bank / Blackbaud set it true; the shared finished/
  // failed below clear it). PayPal (usePaypal.process) also raises it,
  // so this one signal covers the whole fiat panel — matching the
  // "do_not_hit_back" Backdrop, which reads the same flag.
  useModalLock(record.processing)

  const finished = ({ token, serializedDonation }) => {
    const { state, ...rest } = donation
    setDonation({
      ...state,
      ...rest,
      ...serializedDonation,
      token,
      partnerItem,
      paymentOption,
    })
    record.setProcessing(false)
  }

  const failed = errors => {
    validations.contact.setErrors(errors)
    record.setProcessing(false)
  }

  const stripeCreditCardProps = blackbaudEnabled
    ? { recaptcha_enabled: false }
    : creditCardProps

  const creditCard = useCreditCard({
    i18n, partner, partnerItem, donation, record, tribute, failed, finished,
    stripe, elements, collect_phone, validateAll, creditCardProps: stripeCreditCardProps,
    creditCardEnabled: fiatPaymentMethods.credit_card && !blackbaudEnabled,
    disable_express_checkout,
    enable_upsell: sympathy_card_enabled,
    stripeKey,
  })

  const blackbaudCheckout = useBlackbaudCheckout({
    i18n, donation, record, failed, finished,
    blackbaudCheckoutProps,
    validate: validateAll,
    collectsDonor: blackbaudCollectsDonor,
    enabled: blackbaudEnabled && paymentOption == 'credit_card',
    // Always 'above' (see blackbaudAppearance): their floating mode reserves
    // label space, making their fields ~58px where ours are ~38, and the only
    // way to match was pinning our height to a sampled pixel value. Contact
    // forces our own labels on to sit alongside — the same move it already
    // makes for Stripe's AddressElement, which also labels above.
    showInputLabels: show_input_labels || blackbaudEnabled,
  })

  const bank = useBank({
    i18n, stripe, donation, record, bankProps,
    failed, finished,
    enable_upsell: sympathy_card_enabled,
    stripeKey,
  })

  const paypal = usePaypal({
    i18n, donation, record, paymentOption, failed, finished,
    paypal: paypalProps.paypal,
  })

  const venmo = useVenmo({
    enabled: fiatPaymentMethods.venmo,
    paypalProps,
    currency: donation.currency.iso_code,
  })

  useEffect(() => {
    if (blackbaudEnabled) {
      setPaymentOptions({ credit_card: fiatPaymentMethods.credit_card })
      return
    }

    // Don't set payment options until everything initializes
    if (fiatPaymentMethods.credit_card
      && typeof creditCard.canMakePayment == 'undefined') return

    // Make sure options render in this specific order
    setPaymentOptions(fiatOrder.reduce((methods, name) => {
      const method = fiatPaymentMethods[name]
      if (method
        && (!method.oneTimeOnly || donation.state.subscription == 'one_time')
        && (!method.usdOnly || donation.currency.iso_code == 'USD')
        && (!method.paypalCurrency ||
            paypalCurrencySupported(donation.currency.iso_code, paypalProps?.currencies))
        && !(name === 'venmo' && !venmo.eligible)
      ) methods[name] = method
      return methods
    }, creditCard.canMakePayment ? {
      paymentRequest:
        creditCard.canMakePayment.applePay ? 'Apple Pay'
        : creditCard.canMakePayment.googlePay ? 'Google Pay'
        : creditCard.canMakePayment.link ? 'Link' : 'Payment Request',
    } : {}))
  }, [
    fiatPaymentMethods,
    blackbaudEnabled,
    donation.state.subscription,
    donation.currency.iso_code,
    paypalProps?.currencies,
    creditCard.canMakePayment,
    venmo.eligible,
  ])

  useEffect(() => {
    if (!paymentOptions) return

    const previousOptions = optionsRef.current
    optionsRef.current = paymentOptions

    const keys = Object.keys(paymentOptions)
    const option = paymentOption
    || (fiatPaymentMethod == 'credit_card' ? null : fiatPaymentMethod)
    if (keys.includes(option)) return

    // Only when the donor had a live selection — `option` also covers the
    // server default on first paint, which is not a switch they'd have seen.
    // Reassigning silently would restate the cover-costs total, since the fee
    // config moves with the method. Label resolves against the OLD options:
    // paymentRequest reads its label back out of them.
    if (paymentOption && keys.length) setSwitchedFrom({
      method: paymentMethodLabel(paymentOption, i18n, previousOptions),
      new_method: paymentMethodLabel(keys[0], i18n, paymentOptions),
      currency: donation.currency.iso_code,
    })
    setPaymentOption(keys[0])
  }, [ paymentOptions ])

  const inputProps = (id, label, {
    transformValue,
    showInputLabels = show_input_labels,
  } = {}) => ({
    ...commonInputProps(label, showInputLabels),
    id,
    value: donation.state[id],
    onChange: e => {
      const value = transformValue?.(e.target.value) ?? e.target.value
      donation.dispatch({ type: 'setFieldValue', field: id, value })
      validations.contact.resetErrorsFor(id)
    },
  })

  const validateAndSubmit = ({ complete }) => {
    const results = validateAll()
    if (results && complete) {
      switch (paymentOption) {
      case 'credit_card':
        return creditCard.process()
      case 'bank':
        return bank.accountId ? bank.process() : bank.openPicker()
      case 'paypal':
      case 'venmo':
        paypal.init()
      }
    }
  }

  const handleSubmit = e => {
    e.preventDefault()
    if (record.processing || !paymentOption) return

    // Blackbaud's own donate button drives the payment; our form has no
    // submit action of its own (e.g. pressing Enter is a no-op).
    if (blackbaudEnabled) return

    if (paymentOption == 'paymentRequest')
      return creditCard.openPaymentSheet(e)
        && creditCard.paymentRequest.show()

    if (donation.collectAddress)
      elements.getElement('address').getValue().then(validateAndSubmit)
    else validateAndSubmit({ complete: true })
  }

  return (
    <div>
      <form onSubmit={handleSubmit} noValidate>
        <Intro i18n={i18n} frequencies={frequencies} />
        {plugins.renderError()}
        {plugins.renderHook('afterIntro', 'fiat', { donation })}
        <Tribute tribute={tribute} show_input_labels={show_input_labels} />
        <h2 className="h5 section-heading section-heading-donation">
          {i18n.t('donation')}
        </h2>
        {beneficiarySelect.render()}
        <Amount
          i18n={i18n}
          donation={donation}
          currencies={currencies}
          commonInputProps={commonInputProps}
          errors={validations.donation.errors}
          show_input_labels={show_input_labels}
          traditional_flat_style={traditional_flat_style}
          multiOrg={beneficiarySelect.multiOrg}
          multiProject={beneficiarySelect.multiProject}
        />
        {plugins.renderHook('afterAmounts', 'fiat', { donation })}
        <Subscription
          i18n={i18n}
          frequencies={frequencies}
          subscription={donation.state.subscription}
          setSubscription={donation.setSubscription}
          show_input_labels={show_input_labels}
        />
        <div className="mb-3">
          {canadianReceipt.render()}
          {giftAid.render()}
        </div>
        {plugins.renderHook('afterSubscription', 'fiat', { donation })}
        {
          tipping.type == 'tipping' &&
          <Tipping
            i18n={i18n}
            donation={donation}
            errors={validations.tip.errors}
            goal_partner_item={goal_partner_item}
            show_input_labels={show_input_labels}
          />
        }
        {
          // Outside the payment section so wallet options, which skip Contact, get it too.
          employerMatching?.enabled &&
          <EmployerMatching
            i18n={i18n}
            employerMatching={employerMatching}
            shares={donation.shares}
          />
        }
        <div className="form-section-payment">
          <h2 className="h5 mt-4 section-heading section-heading-payment">
            {i18n.t('payment')}
          </h2>
          <PaymentOptions
            i18n={i18n}
            paymentOptions={paymentOptions}
            paymentOption={paymentOption}
            setPaymentOption={option => {
              setSwitchedFrom(null)
              setPaymentOption(option)
            }}
            switchedFrom={switchedFrom}
            show_input_labels={show_input_labels}
            errors={validations.contact.errors}
          />
          {
            paymentOption && paymentOption != 'paymentRequest' &&
            <Contact
              i18n={i18n}
              session={session}
              collect_phone={collect_phone}
              show_input_labels={show_input_labels}
              donation={donation}
              canadianReceipt={canadianReceipt}
              giftAid={giftAid}
              inputProps={inputProps}
              googleApiKey={googleApiKey}
              errors={validations.contact.errors}
              useAddressElement={!blackbaudEnabled}
              blackbaudEnabled={blackbaudEnabled}
              hideDonorFields={hideDonorFields}
              hideAddress={[ 'crypto', 'deferred' ].includes(panel)}
            />
          }
          {
            paymentOption == 'credit_card' && blackbaudEnabled &&
            <BlackbaudCheckout
              i18n={i18n}
              donation={donation}
              record={record}
              checkout={blackbaudCheckout}
              errors={validations.contact.errors}
            />
          }
          {
            paymentOption == 'credit_card' && !blackbaudEnabled &&
            <CreditCard
              i18n={i18n}
              show_input_labels={show_input_labels}
              stripeElements={stripeElements}
              donation={donation}
              errors={validations.contact.errors}
              resetStripeErrors={() => validations.contact.resetErrorsFor('stripe')}
            />
          }
          {
            paymentOption &&
            <div className="mb-3">
              <DataSharing
                i18n={i18n}
                foundation={foundation}
                beneficiary={partnerItem.beneficiary}
                partner={partner}
                partner_is_pledge={partner_is_pledge}
                data_sharing={data_sharing}
                defaultShares={defaultShares}
                shares={donation.shares}
                setShares={donation.setShares}
                collect_phone={collect_phone}
                collect_address={collect_address}
              />
              <EmployerMatchingStatus
                i18n={i18n}
                employerMatching={employerMatching}
                shares={donation.shares}
              />
              {plugins.renderHook('afterDataSharing', 'fiat', { donation })}
              <CoverCosts i18n={i18n} donation={donation} tipping={tipping} />
            </div>
          }
          {
            creditCardProps?.virtualTerminal &&
            Object.keys(paymentOptions || {}).length <= 1 &&
            <InvalidForm>{validations.contact.errors?.base?.[0]}</InvalidForm>
          }
          {
            !blackbaudEnabled &&
            <SubmitButton
              i18n={i18n}
              donation={donation}
              record={record}
              creditCard={creditCard}
              bank={bank}
              paypal={paypal}
              foundation={foundation}
              paymentOption={paymentOption}
            />
          }
        </div>
      </form>
      <Backdrop show={paypal.showSummary} position="bottom">
        <Summary
          i18n={i18n}
          paypalConfig={paypalProps.paypal}
          donation={donation}
          paypalProcessor={paypal}
          venmo={venmo}
          validation={validations.contact}
          paymentOption={paymentOption}
          partnerItem={partnerItem}
          dismissSummary={() => paypal.setShowSummary(false)}
        />
      </Backdrop>
      <Backdrop show={record.processing} position="center">
        <Processing>
          <h2 className="h5 text-primary modal-heading modal-heading-processing">
            {i18n.t('processing')}
          </h2>
          <p className="mb-0">{i18n.t('do_not_hit_back')}</p>
        </Processing>
      </Backdrop>
      {blackbaudEnabled ? blackbaudCheckout.renderRecaptcha() : creditCard.renderRecaptcha()}
    </div>
  )
}

export default App
